// ---- Content validation ----
// Two layers over the generated manifest:
//   1. structural — each document against its zod schema (schemas.ts)
//   2. referential — cross-file id checks (quest npcIds resolve, duplicate ids, …)
// Every finding carries file + JSON path + message (with a did-you-mean when a
// near-miss exists) so an author — human or Claude — can fix it in one hop.
//
// Runs: module-load fail-fast via load.ts (server boot AND client bundle),
// `bun run validate`, `bun test`, and the studio's content PUT endpoint.
// Dependency-light on purpose (zod + manifest types only): while migration is in
// flight, ids that still live in TS registries (NPC spawn ids, item types) are
// passed in as ExternalRefs by the caller instead of imported here.

import { SCHEMAS, EDGE_SIDES, OPPOSITE_SIDE, type DomainName, type PartGroupDoc } from "./schemas.ts";
import { resolveStructureDoc } from "../structure/params.ts";
import type { RawEntry } from "./manifest.ts";
import {
  resolvePalette, colorOf, ARCHETYPE_ANCHORS, ARCHETYPE_SLOTS, WEAR_FILLER_ANCHORS,
  type ModelDoc,
} from "../model/parts.ts";
import { resolveModelDoc } from "../model/variants.ts";
import { resolveNpcDoc } from "../npc/inherit.ts";
import { CODED_MODELS } from "../model/coded.ts";
import { codedWearDef } from "../wear/index.ts";
import { WILDLIFE_FLEE } from "../npc/creature.ts";
import { FURNITURE_PIECES } from "../structure/furniture/pieces.ts";
import { REQUIRED_CATCH_TABLES } from "../sim/fishing.ts";
import {
  checkHoldSeat,
  mergeHold,
  gripDeltaMagnitude,
  holdOffsetLen,
  GRIP_DELTA_WARN,
  GRIP_DELTA_WARN_2H_CARRY,
  OFFSET_WARN,
  type HoldLike,
  type Landmarks,
} from "../anim/hold-seat.ts";
import {
  WEAPON_ARCHETYPE_IDS,
  WEAPON_ARCHETYPES,
  isWeaponArchetypeId,
  type WeaponArchetypeId,
} from "../anim/archetypes.ts";
import { actionBlocks } from "../cinematic/timeline.ts";
import { BODY_POSES } from "../anim/clips.ts";

export type ContentIssue = { file: string; path: string; message: string };

export type ExternalRefs = {
  /** NPC spawn ids (from the TS roster until NPCs are content-managed). */
  npcIds: Set<string>;
  /** Item types (from shared.ts until items are content-managed). */
  itemTypes: Set<string>;
};

export type RawContent = {
  quests: RawEntry[];
  npcs: RawEntry[];
  items: RawEntry[];
  models: RawEntry[];
  decos: RawEntry[];
  parts: RawEntry[];
  structures: RawEntry[];
  shops: RawEntry[];
  skills: RawEntry[];
  standing: RawEntry[];
  moves: RawEntry[];
  scenarios: RawEntry[];
  cinematics: RawEntry[];
  "catch-tables": RawEntry[];
  "scatter-palettes": RawEntry[];
  tunables: RawEntry[];
  maps: RawEntry[];
  /** content/world holds ONE file (factions.json) since plan 20 folded the
   *  placements into the map docs — a singleton, exactly like `anim`. */
  world: RawEntry | null;
  anim: RawEntry | null;
};

// ---------------------------------------------------------------------------

/** "did you mean" support: nearest candidate within edit distance 2. */
function suggest(input: string, candidates: Iterable<string>): string {
  let best = "", bestD = 3;
  for (const c of candidates) {
    const d = editDistance(input, c, bestD);
    if (d < bestD) { bestD = d; best = c; }
  }
  return best ? ` — did you mean "${best}"?` : "";
}

function editDistance(a: string, b: string, cap: number): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  const prev = new Array(b.length + 1).fill(0).map((_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0]!;
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j]!;
      prev[j] = Math.min(prev[j]! + 1, prev[j - 1]! + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[b.length]!;
}

function zodPath(path: PropertyKey[]): string {
  return path.reduce<string>(
    (acc, p) => (typeof p === "number" ? `${acc}[${p}]` : acc ? `${acc}.${String(p)}` : String(p)),
    "",
  );
}

/** ONE rule for a degenerate box roam (min not < max on both axes) — shared by
 *  the npc-doc roam check and the map-doc spawn roam check. */
function badRoamBox(r?: { mode: string } & Partial<Record<"minX" | "maxX" | "minZ" | "maxZ", number>>): boolean {
  return r?.mode === "box" && (r.minX! >= r.maxX! || r.minZ! >= r.maxZ!);
}

// ---------------------------------------------------------------------------

/** Structural pass: one domain's docs against its schema. Returns parsed docs
 *  (only the ones that passed) alongside any issues. */
function structural<T>(
  entries: RawEntry[],
  domain: DomainName,
  errors: ContentIssue[],
): { file: string; doc: T }[] {
  const schema = SCHEMAS[domain];
  const ok: { file: string; doc: T }[] = [];
  for (const e of entries) {
    const res = schema.safeParse(e.data);
    if (res.success) {
      ok.push({ file: e.file, doc: res.data as T });
    } else {
      for (const issue of res.error.issues) {
        errors.push({ file: e.file, path: zodPath(issue.path), message: issue.message });
      }
    }
  }
  return ok;
}

export type ValidationResult = { errors: ContentIssue[]; warnings: ContentIssue[] };

export function validateAll(raw: RawContent, ext: ExternalRefs): ValidationResult {
  const errors: ContentIssue[] = [];
  const warnings: ContentIssue[] = [];

  // Structural-parse every domain FIRST, then cross-ref — npc↔quest references
  // are mutual (quest objectives name spawns; talk actions name quests).
  type QuestDoc = import("./schemas.ts").QuestDoc;
  type NpcDoc = import("./schemas.ts").NpcDoc;
  type MapDoc = import("./schemas.ts").MapDoc;
  const quests = structural<QuestDoc>(raw.quests, "quest", errors);
  const npcs = structural<NpcDoc>(raw.npcs, "npc", errors);
  // Maps parse up here too: spawn ids live in the map docs' `npcs` arrays
  // (plan 21), and the quest pass below needs them. The spatial maps pass
  // further down reuses this parse.
  const mapDocs = structural<MapDoc>(raw.maps, "map", errors);

  // Id sets used by the cross-ref passes below. While a domain is still TS
  // (mid-migration), its ids come from ExternalRefs instead.
  const npcIds = mapDocs.length
    ? new Set(mapDocs.flatMap((m) => m.doc.npcs.map((s) => s.id)))
    : ext.npcIds;
  // Cross-map lookups for the portal/edge passes (plan 24): a portal's `to`
  // must resolve, its spawn must land in the TARGET's bounds, and edge links
  // need the neighbor's size + reciprocal link — all order-independent.
  const mapDocById = new Map(mapDocs.map((m) => [m.doc.id, m.doc]));
  const questSteps = new Map<string, Set<string>>(); // quest id → step ids
  for (const { doc } of quests) questSteps.set(doc.id, new Set(doc.steps.map((s) => s.id)));

  // -- quests ---------------------------------------------------------------
  const questIds = new Map<string, string>(); // id → file
  for (const { file, doc } of quests) {
    const prev = questIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate quest id "${doc.id}" (also in ${prev})` });
    else questIds.set(doc.id, file);

    const stepIds = new Set<string>();
    doc.steps.forEach((step, i) => {
      if (stepIds.has(step.id))
        errors.push({ file, path: `steps[${i}].id`, message: `duplicate step id "${step.id}"` });
      stepIds.add(step.id);
      const obj = step.objective;
      if ((obj.kind === "kill" || obj.kind === "talk" || obj.kind === "deliver") && !npcIds.has(obj.npcId)) {
        errors.push({
          file,
          path: `steps[${i}].objective.npcId`,
          message: `unknown NPC "${obj.npcId}"${suggest(obj.npcId, npcIds)}`,
        });
      }
      // item/deliver type refs are checked after `itemTypes` is built (items pass).
    });
  }

  // -- parts library (slot fillers) --------------------------------------------
  const partGroups = structural<PartGroupDoc>(raw.parts, "part", errors);
  const partGroupById = new Map<string, PartGroupDoc>();

  /** Shared anchor-filler check — the parts doc must exist and fit the
   *  archetype. Used by model `slots` fillers AND item `wear.part` refs, so the
   *  wording/semantics of "doesn't fit" stay in one place. */
  const checkPartFits = (file: string, path: string, filler: string, archetype: string): void => {
    const group = partGroupById.get(filler);
    if (!group) {
      errors.push({ file, path, message: `unknown parts-library doc "${filler}"${suggest(filler, partGroupById.keys())}` });
    } else if (!(group.fits as string[]).includes(archetype)) {
      errors.push({ file, path, message: `"${filler}" doesn't fit ${archetype} (fits: ${group.fits.join(", ")})` });
    }
  };
  for (const { file, doc } of partGroups) {
    if (partGroupById.has(doc.id)) {
      errors.push({ file, path: "id", message: `duplicate parts-library id "${doc.id}"` });
      continue;
    }
    partGroupById.set(doc.id, doc);

    let palette: Record<string, number> = {};
    try {
      palette = resolvePalette(doc.palette ?? {});
    } catch (e) {
      errors.push({ file, path: "palette", message: (e as Error).message });
      continue;
    }
    doc.parts.forEach((part, i) => {
      try {
        colorOf(part.color, palette);
      } catch {
        errors.push({
          file,
          path: `parts[${i}].color`,
          message: `"${part.color}" is neither a palette key nor #rrggbb${suggest(part.color, Object.keys(palette))}`,
        });
      }
      // mirror doubles into ONE limb group — pads need explicit L/R + limb.
      if (part.mirror && part.limb) {
        errors.push({
          file,
          path: `parts[${i}]`,
          message:
            `mirror + limb ("${part.limb}") is invalid — both sides would parent under the same joint. ` +
            `Emit explicit L/R prims with limb "shoulderL" / "shoulderR" (or the matching limb) instead`,
        });
      }
      // Optional offset base when the host passes full anchors (torsowear fill).
      // Default remains root = slot seat. Unknown names are rejected here.
      if (part.anchor && !(WEAR_FILLER_ANCHORS as readonly string[]).includes(part.anchor)) {
        errors.push({
          file,
          path: `parts[${i}].anchor`,
          message:
            `unknown filler anchor "${part.anchor}" — use one of: ${WEAR_FILLER_ANCHORS.join(", ")} ` +
            `(default omit = slot root; shoulder pads usually pair anchor shoulderShelfL/R with limb)`,
        });
      }
    });
  }

  // -- models -----------------------------------------------------------------
  const models = structural<ModelDoc>(raw.models, "model", errors);

  const modelIds = new Map<string, string>(); // id → file
  const modelById = new Map<string, ModelDoc>();
  /** Resolved (base-merged) model docs — hold seating + item grip checks use these. */
  const resolvedModels = new Map<string, ModelDoc>();
  for (const { doc } of models) if (!modelById.has(doc.id)) modelById.set(doc.id, doc);
  // Socket-slot fillers are ITEM types — items parse later; check these after.
  const pendingSocketFillers: { file: string; path: string; filler: string }[] = [];

  for (const { file, doc: rawDoc } of models) {
    const prev = modelIds.get(rawDoc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate model id "${rawDoc.id}" (also in ${prev})` });
    else modelIds.set(rawDoc.id, file);

    // Variants: flatten against the base FIRST — every check below runs on the
    // merged doc (a variant's palette keys may come from its base, its body
    // must be complete only after merging, etc.).
    let doc = rawDoc;
    if (rawDoc.base) {
      try {
        doc = resolveModelDoc(rawDoc, (b) => modelById.get(b));
      } catch (e) {
        errors.push({ file, path: "base", message: (e as Error).message });
        continue; // everything below needs the merged doc
      }
      // The merged doc must satisfy the FULL model shape (incl. the archetype-
      // specific body check the structural pass skips for partial variants).
      const merged = SCHEMAS.model.safeParse({ ...doc, base: undefined });
      if (!merged.success) {
        for (const i of merged.error.issues) {
          errors.push({ file, path: `(merged) ${zodPath(i.path)}`, message: i.message });
        }
      }
    }
    resolvedModels.set(rawDoc.id, doc);

    // Slots: names must be archetype slots; anchor fillers must be parts-library
    // docs that fit this archetype; socket fillers are checked against items below.
    const slotDefs = ARCHETYPE_SLOTS[doc.archetype];
    for (const [slot, filler] of Object.entries(doc.slots ?? {})) {
      const def = slotDefs[slot];
      const at = `slots.${slot}`;
      if (!def) {
        errors.push({
          file, path: at,
          message: `"${slot}" is not a ${doc.archetype} slot (have: ${Object.keys(slotDefs).join(", ") || "none"})${suggest(slot, Object.keys(slotDefs))}`,
        });
        continue;
      }
      if ("socket" in def) {
        pendingSocketFillers.push({ file, path: at, filler });
      } else {
        checkPartFits(file, at, filler, doc.archetype);
      }
    }

    // Variation sanity — hard caps live in the schema; warn on values that read
    // as "different character" rather than "natural variety".
    if (doc.variation?.shade !== undefined && doc.variation.shade > 0.2) {
      warnings.push({ file, path: "variation.shade", message: `shade jitter ${doc.variation.shade} is a lot — instances may read as different palettes` });
    }
    if (doc.variation?.scale !== undefined && doc.variation.scale > 0.05) {
      warnings.push({ file, path: "variation.scale", message: `scale jitter ${doc.variation.scale} is a lot — colliders/nameplates don't scale with it` });
    }

    // Palette must fully resolve (derived entries, circular refs).
    let palette: Record<string, number> = {};
    try {
      palette = resolvePalette(doc.palette ?? {});
    } catch (e) {
      errors.push({ file, path: "palette", message: (e as Error).message });
      continue; // color checks below would cascade
    }

    const anchorNames = ARCHETYPE_ANCHORS[doc.archetype];
    (doc.parts ?? []).forEach((part, i) => {
      try {
        colorOf(part.color, palette);
      } catch {
        errors.push({
          file,
          path: `parts[${i}].color`,
          message: `"${part.color}" is neither a palette key nor #rrggbb${suggest(part.color, Object.keys(palette))}`,
        });
      }
      if (part.anchor && !anchorNames.includes(part.anchor)) {
        errors.push({
          file,
          path: `parts[${i}].anchor`,
          message: `"${part.anchor}" is not a ${doc.archetype} anchor (have: ${anchorNames.join(", ")})${suggest(part.anchor, anchorNames)}`,
        });
      }
    });

    // Body colors (palette keys) must resolve too. Every string in a body is a
    // color except the enum discriminators — walk recursively (lower.color etc.).
    const walkBody = (obj: Record<string, unknown>, prefix: string) => {
      for (const [k, v] of Object.entries(obj)) {
        if (k === "kind" || k === "hairStyle" || k === "beardStyle" || k === "sex" || k === "build") continue;
        if (typeof v === "string") {
          try {
            colorOf(v, palette);
          } catch {
            errors.push({
              file,
              path: `${prefix}${k}`,
              message: `"${v}" is neither a palette key nor #rrggbb${suggest(v, Object.keys(palette))}`,
            });
          }
        } else if (v && typeof v === "object" && !Array.isArray(v)) {
          walkBody(v as Record<string, unknown>, `${prefix}${k}.`);
        }
      }
    };
    walkBody(doc.body ?? {}, "body.");
  }

  const knownModels = new Set([...modelIds.keys(), ...Object.keys(CODED_MODELS)]);

  // -- items ------------------------------------------------------------------
  type ItemDoc = import("./schemas.ts").ItemDoc;
  const items = structural<ItemDoc>(raw.items, "item", errors);

  // Once items are content-managed, the closed type set derives FROM them; the
  // external set only backs validation while the domain is empty (mid-migration).
  const itemTypes = items.length ? new Set(items.map((i) => i.doc.type)) : ext.itemTypes;

  // Quest item/deliver objectives + coach.equip/select types → real item types
  // (deferred until itemTypes exists).
  for (const { file, doc } of quests) {
    doc.steps.forEach((step, i) => {
      const obj = step.objective;
      if (obj.kind === "item" || obj.kind === "deliver") {
        obj.types.forEach((t, ti) => {
          if (!itemTypes.has(t)) {
            errors.push({
              file,
              path: `steps[${i}].objective.types[${ti}]`,
              message: `unknown item type "${t}"${suggest(t, itemTypes)}`,
            });
          }
        });
      }
      if (step.coach?.equip && !itemTypes.has(step.coach.equip.type)) {
        errors.push({
          file,
          path: `steps[${i}].coach.equip.type`,
          message: `unknown item type "${step.coach.equip.type}"${suggest(step.coach.equip.type, itemTypes)}`,
        });
      }
      if (step.coach?.select && !itemTypes.has(step.coach.select.type)) {
        errors.push({
          file,
          path: `steps[${i}].coach.select.type`,
          message: `unknown item type "${step.coach.select.type}"${suggest(step.coach.select.type, itemTypes)}`,
        });
      }
      // Fixed `point` wins over nearest-* — warn so authors don't think both apply.
      if (step.coach?.point && (step.coach.pointNearestChop || step.coach.pointNearestFire)) {
        warnings.push({
          file,
          path: `steps[${i}].coach`,
          message: `coach.point is set — pointNearestChop/Fire are ignored (resolve order: point wins)`,
        });
      }
    });
  }

  const seenItemTypes = new Map<string, string>();
  for (const { file, doc } of items) {
    const prev = seenItemTypes.get(doc.type);
    if (prev) errors.push({ file, path: "type", message: `duplicate item type "${doc.type}" (also in ${prev})` });
    else seenItemTypes.set(doc.type, file);

    if (doc.model !== undefined && !knownModels.has(doc.model)) {
      errors.push({ file, path: "model", message: `unknown model "${doc.model}"${suggest(doc.model, knownModels)}` });
    }
    // Wearables: the worn parts doc must exist and fit humanoids (the only
    // archetype with wear slots — the paperdoll is humanoid-shaped by design).
    if (doc.wear?.part !== undefined) {
      checkPartFits(file, "wear.part", doc.wear.part, "humanoid");
    }
    if (doc.wear?.coded !== undefined && !codedWearDef(doc.wear.coded)) {
      errors.push({
        file,
        path: "wear.coded",
        message: `unknown coded wearable "${doc.wear.coded}" — register it in src/wear/index.ts CODED_WEAR`,
      });
    }
    const w = doc.weapon;
    if (w?.ranged) {
      if (!itemTypes.has(w.ranged.ammo)) {
        errors.push({
          file,
          path: "weapon.ranged.ammo",
          message: `unknown item type "${w.ranged.ammo}"${suggest(w.ranged.ammo, itemTypes)}`,
        });
      }
      if (w.style !== "draw") {
        warnings.push({ file, path: "weapon.style", message: `ranged weapons normally use style "draw" (got "${w.style}")` });
      }
      const lastBand = w.ranged.bands[w.ranged.bands.length - 1];
      if (lastBand && lastBand.max !== w.attackRange) {
        warnings.push({
          file,
          path: "weapon.attackRange",
          message: `attackRange (${w.attackRange}) should equal the last band's max (${lastBand.max}) — it's the hard fire cap`,
        });
      }
    }
    // Cooking (plan 18): a raw's cook.into must resolve to a real (cooked) item type.
    if (doc.cook && !itemTypes.has(doc.cook.into)) {
      errors.push({
        file,
        path: "cook.into",
        message: `unknown item type "${doc.cook.into}"${suggest(doc.cook.into, itemTypes)}`,
      });
    }

    // Hold seating: weapon / shield / fishing.rod each pin landmarks.grip to a
    // hand socket. Model owns the grip point; hold.hilt must match; secondary
    // landmarks must sit farther from the fist than grip after seating.
    type HoldBlock = { hold: HoldLike; path: string };
    const holds: HoldBlock[] = [];
    if (doc.weapon?.hold) holds.push({ hold: doc.weapon.hold, path: "weapon.hold" });
    if (doc.shield?.hold) holds.push({ hold: doc.shield.hold, path: "shield.hold" });
    if (doc.fishing?.hold) holds.push({ hold: doc.fishing.hold, path: "fishing.hold" });
    for (const { hold, path } of holds) {
      if (!doc.model) {
        errors.push({
          file,
          path,
          message: `held item needs a model (with landmarks.grip) so the grip seat can be validated`,
        });
        continue;
      }
      const mdoc = resolvedModels.get(doc.model);
      if (!mdoc) continue; // unknown model already reported above
      const lm = mdoc.landmarks as Landmarks | undefined;
      for (const issue of checkHoldSeat(hold, lm, { pathPrefix: path })) {
        let message = issue.message;
        // Item hold may be overridden by clips archetypes.<id>.hold at runtime —
        // large item offsets are often intentional 2H carry that clips re-seat.
        if (
          issue.level === "warn" &&
          issue.path.includes("offset") &&
          doc.weapon &&
          doc.weapon.archetype
        ) {
          message +=
            ` — note: content/anim/clips.json archetypes.${doc.weapon.archetype}.hold may override offset/rot at runtime (effective seat is item + override)`;
        }
        (issue.level === "error" ? errors : warnings).push({
          file,
          path: issue.path.startsWith("landmarks") ? `model "${doc.model}" → ${issue.path}` : issue.path,
          message,
        });
      }
    }
  }

  // weapon.moves reference move docs, which parse below (after skills — a move's
  // requiresBox needs the box-id set). Collect the refs, check at the end.
  const pendingWeaponMoves: { file: string; path: string; id: string }[] = [];
  for (const { file, doc } of items) {
    (doc.weapon?.moves ?? []).forEach((m, i) => {
      pendingWeaponMoves.push({ file, path: `weapon.moves[${i}]`, id: m });
    });
  }

  // Socket-slot fillers (held/offhand) must be equippable items — weapons, or
  // held tools (fishing rod / hatchet) which seat into handMain via equipTool.
  for (const { file, path, filler } of pendingSocketFillers) {
    if (!itemTypes.has(filler)) {
      errors.push({ file, path, message: `unknown item type "${filler}"${suggest(filler, itemTypes)}` });
    } else {
      const item = items.find((i) => i.doc.type === filler);
      if (item && !item.doc.weapon && !item.doc.fishing && !item.doc.chopping) {
        errors.push({
          file, path,
          message: `"${filler}" has no weapon/fishing/chopping block — socket slots hold equippables`,
        });
      }
    }
  }

  // -- world: factions + stance table (parsed BEFORE npcs — faction tags ref it) --
  // `player` is the implicit player-side id: usable in stance rows and scenario
  // overrides, but never declared (and never tagged onto an NPC).
  const factionIds = new Set<string>();
  let haveFactionsDoc = false;
  const worldFactions = raw.world;
  if (worldFactions) {
    const res = SCHEMAS["world-factions"].safeParse(worldFactions.data);
    if (!res.success) {
      for (const issue of res.error.issues) {
        errors.push({ file: worldFactions.file, path: zodPath(issue.path), message: issue.message });
      }
    } else {
      haveFactionsDoc = true;
      res.data.factions.forEach((f, i) => {
        if (f.id === "player") {
          errors.push({ file: worldFactions.file, path: `factions[${i}].id`, message: `"player" is the implicit player-side faction — don't declare it` });
        } else if (factionIds.has(f.id)) {
          errors.push({ file: worldFactions.file, path: `factions[${i}].id`, message: `duplicate faction id "${f.id}"` });
        }
        factionIds.add(f.id);
      });
      const stanceSides = new Set([...factionIds, "player"]);
      const seenPairs = new Set<string>();
      res.data.stances.forEach((s, i) => {
        for (const side of ["a", "b"] as const) {
          if (!stanceSides.has(s[side])) {
            errors.push({ file: worldFactions.file, path: `stances[${i}].${side}`, message: `unknown faction "${s[side]}"${suggest(s[side], stanceSides)}` });
          }
        }
        if (s.a === s.b) {
          errors.push({ file: worldFactions.file, path: `stances[${i}]`, message: `a faction is always friendly to itself — drop the self-pair` });
        }
        const pair = s.a < s.b ? `${s.a}|${s.b}` : `${s.b}|${s.a}`;
        if (seenPairs.has(pair)) {
          errors.push({ file: worldFactions.file, path: `stances[${i}]`, message: `duplicate stance pair ${s.a}/${s.b}` });
        }
        seenPairs.add(pair);
      });
    }
  }

  // -- npcs -----------------------------------------------------------------
  const seenNpcDocIds = new Map<string, string>();
  const npcById = new Map<string, NpcDoc>();
  for (const { doc } of npcs) if (!npcById.has(doc.id)) npcById.set(doc.id, doc);
  // openShop actions reference a shop id, but shops are parsed AFTER npcs — collect
  // the refs here and cross-check once seenShopIds is built (like pendingSocketFillers).
  const pendingOpenShops: { file: string; path: string; id: string }[] = [];
  // playCinematic actions reference a cinematic id — same deferred pattern
  // (cinematics parse after npcs).
  const pendingPlayCinematics: { file: string; path: string; id: string }[] = [];
  for (const { file, doc: rawDoc } of npcs) {
    const prevDoc = seenNpcDocIds.get(rawDoc.id);
    if (prevDoc) errors.push({ file, path: "id", message: `duplicate npc id "${rawDoc.id}" (also in ${prevDoc})` });
    else seenNpcDocIds.set(rawDoc.id, file);

    // Variants: flatten against the base FIRST — every check below runs on the
    // MERGED doc (a variant inherits model/combat/faction/territory from its base).
    let doc = rawDoc;
    if (rawDoc.base) {
      try {
        doc = resolveNpcDoc(rawDoc, (b) => npcById.get(b));
      } catch (e) {
        errors.push({ file, path: "base", message: (e as Error).message });
        continue; // everything below needs the merged doc
      }
      // The merged doc must satisfy the FULL npc shape (the core fields the
      // structural pass lets a variant omit must all be present after merging).
      const merged = SCHEMAS.npc.safeParse({ ...doc, base: undefined });
      if (!merged.success) {
        for (const i of merged.error.issues) {
          errors.push({ file, path: `(merged) ${zodPath(i.path)}`, message: i.message });
        }
      }
    }

    if (doc.model !== undefined && !knownModels.has(doc.model)) {
      errors.push({ file, path: "model", message: `unknown model "${doc.model}"${suggest(doc.model, knownModels)}` });
    }

    (doc.loot ?? []).forEach((l, i) => {
      if (!itemTypes.has(l.type)) {
        errors.push({ file, path: `loot[${i}].type`, message: `unknown item type "${l.type}"${suggest(l.type, itemTypes)}` });
      }
      if (l.min !== undefined && l.max !== undefined && l.min > l.max) {
        errors.push({ file, path: `loot[${i}]`, message: `min (${l.min}) > max (${l.max})` });
      }
    });
    if (doc.loot?.length && !doc.combat) {
      warnings.push({ file, path: "loot", message: "loot needs a combat block to ever drop (NPC can't be defeated)" });
    }

    // Plan 14: faction tags resolve against the declared factions; a territory
    // whose leash doesn't exceed its radius drops targets it just acquired.
    if (doc.faction !== undefined) {
      if (doc.faction === "player") {
        errors.push({ file, path: "faction", message: `"player" is the implicit player side — NPCs can't join it` });
      } else if (haveFactionsDoc && !factionIds.has(doc.faction)) {
        errors.push({ file, path: "faction", message: `unknown faction "${doc.faction}"${suggest(doc.faction, factionIds)}` });
      } else if (!haveFactionsDoc) {
        warnings.push({ file, path: "faction", message: `no content/world/factions.json — faction "${doc.faction}" has no stance table to resolve against` });
      }
    }
    if (doc.territory) {
      if (doc.territory.leash <= doc.territory.radius) {
        errors.push({ file, path: "territory.leash", message: `leash (${doc.territory.leash}) must exceed radius (${doc.territory.radius}) — else it drops targets the moment it acquires them` });
      }
      if (!doc.combat) {
        warnings.push({ file, path: "territory", message: "territory needs a combat block to ever attack" });
      }
    }
    if (doc.assistRadius !== undefined && doc.faction === undefined) {
      warnings.push({ file, path: "assistRadius", message: "assistRadius does nothing without a faction (assist scans faction-mates)" });
    }
    if (doc.combat?.ranged) {
      const lastBand = doc.combat.ranged.bands[doc.combat.ranged.bands.length - 1]!;
      const reach = doc.combat.attackRange ?? 2.2;
      if (lastBand.max <= reach) {
        warnings.push({ file, path: "combat.ranged.bands", message: `fire range (${lastBand.max}) doesn't exceed melee reach (${reach}) — the archer will never shoot` });
      }
    }

    if (doc.flee) {
      // Defaults mirror what the loader merges in (WILDLIFE_FLEE).
      const alert = doc.flee.alertRadius ?? WILDLIFE_FLEE.alertRadius!;
      const safe = doc.flee.safeRadius ?? WILDLIFE_FLEE.safeRadius!;
      if (safe <= alert) {
        warnings.push({ file, path: "flee.safeRadius", message: `safeRadius (${safe}) must exceed alertRadius (${alert}) — the gap is the anti-flicker hysteresis band` });
      }
      if (!doc.roam) {
        warnings.push({ file, path: "flee", message: "fleeing NPCs need a roam block to settle back into" });
      }
    }

    if (badRoamBox(doc.roam)) {
      errors.push({ file, path: "roam", message: "roam box min must be < max on both axes" });
    }

    // Talk graph: root + choice targets resolve, node ids unique, everything
    // reachable, actions/conditions reference real quests + steps + items.
    const talk = doc.talk;
    if (talk) {
      const nodeIds = new Map<string, number>();
      talk.nodes.forEach((n, i) => {
        if (nodeIds.has(n.id)) errors.push({ file, path: `talk.nodes[${i}].id`, message: `duplicate node id "${n.id}"` });
        else nodeIds.set(n.id, i);
      });
      if (!nodeIds.has(talk.root)) {
        errors.push({ file, path: "talk.root", message: `unknown node "${talk.root}"${suggest(talk.root, nodeIds.keys())}` });
      }
      const reachable = new Set<string>();
      // Root + entry-gated nodes (node.when) are all open points — entry nodes
      // are never linked from root but still open via resolveTalkRoot.
      const queue: string[] = [];
      if (nodeIds.has(talk.root)) queue.push(talk.root);
      for (const n of talk.nodes) if (n.when && nodeIds.has(n.id)) queue.push(n.id);
      while (queue.length) {
        const id = queue.pop()!;
        if (reachable.has(id)) continue;
        reachable.add(id);
        const node = talk.nodes[nodeIds.get(id)!]!;
        for (const c of node.choices ?? []) if (c.to && nodeIds.has(c.to)) queue.push(c.to);
      }
      const checkWhen = (
        when: { quest: string; is: string; step?: string; has?: { types: string[] } },
        at: string,
      ) => {
        const steps = questSteps.get(when.quest);
        if (!steps) {
          errors.push({ file, path: `${at}.quest`, message: `unknown quest "${when.quest}"${suggest(when.quest, questSteps.keys())}` });
        } else if (when.step !== undefined && !steps.has(when.step)) {
          errors.push({ file, path: `${at}.step`, message: `quest "${when.quest}" has no step "${when.step}"${suggest(when.step, steps)}` });
        }
        if (when.step !== undefined && when.is !== "active") {
          warnings.push({ file, path: `${at}.step`, message: `"step" only applies when is:"active"` });
        }
        if (when.has) {
          for (const t of when.has.types) {
            if (!itemTypes.has(t)) {
              errors.push({ file, path: `${at}.has.types`, message: `unknown item type "${t}"${suggest(t, itemTypes)}` });
            }
          }
        }
      };
      talk.nodes.forEach((n, i) => {
        if (nodeIds.get(n.id) === i && !reachable.has(n.id)) {
          warnings.push({ file, path: `talk.nodes[${i}]`, message: `node "${n.id}" is unreachable from root "${talk.root}"` });
        }
        if (n.when) checkWhen(n.when, `talk.nodes[${i}].when`);
        (n.choices ?? []).forEach((c, j) => {
          const at = `talk.nodes[${i}].choices[${j}]`;
          if (c.to && !nodeIds.has(c.to)) {
            errors.push({ file, path: `${at}.to`, message: `unknown node "${c.to}"${suggest(c.to, nodeIds.keys())}` });
          }
          const a = c.action;
          if (a && (a.kind === "startQuest" || a.kind === "advanceQuest") && !questSteps.has(a.id)) {
            errors.push({ file, path: `${at}.action.id`, message: `unknown quest "${a.id}"${suggest(a.id, questSteps.keys())}` });
          }
          if (a?.kind === "giveItem" && !itemTypes.has(a.type)) {
            errors.push({ file, path: `${at}.action.type`, message: `unknown item type "${a.type}"${suggest(a.type, itemTypes)}` });
          }
          if (a?.kind === "openShop") {
            pendingOpenShops.push({ file, path: `${at}.action.id`, id: a.id });
          }
          if (a?.kind === "playCinematic") {
            pendingPlayCinematics.push({ file, path: `${at}.action.id`, id: a.id });
            // playCinematic ends the conversation (talk.ts endTalk before the
            // world swap), so a paired `to` node would silently never render.
            if (c.to) {
              errors.push({ file, path: `${at}.to`, message: `a playCinematic choice can't also route \`to\` a node — the conversation ends when the cinematic starts; drop \`to\`` });
            }
          }
          if (c.when) checkWhen(c.when, `${at}.when`);
        });
      });
    }
  }

  // -- quest ↔ dialogue contracts (after all NPC talk graphs exist) ------------
  // Contract 1: every deliver step needs ≥1 choice with when.step + when.has
  // whose type set matches the objective (so empty-bag turn-in lines stay hidden).
  for (const { file, doc } of quests) {
    doc.steps.forEach((step, i) => {
      if (step.objective.kind !== "deliver") return;
      const need = new Set(step.objective.types);
      let found = false;
      for (const { doc: npc } of npcs) {
        if (!npc.talk) continue;
        for (const node of npc.talk.nodes) {
          for (const c of node.choices ?? []) {
            const w = c.when;
            if (!w || w.quest !== doc.id || w.step !== step.id || !w.has) continue;
            const has = new Set(w.has.types);
            if (need.size === has.size && [...need].every((t) => has.has(t))) found = true;
          }
        }
      }
      if (!found) {
        warnings.push({
          file,
          path: `steps[${i}]`,
          message: `deliver step "${step.id}" has no dialogue choice with matching when.has for types [${step.objective.types.join(", ")}] (quest-authoring contract 1)`,
        });
      }
    });
  }

  // Soft-landing product pins (warnings — the starter is the golden path).
  {
    const sl = quests.find((q) => q.doc.id === "soft-landing");
    if (sl) {
      const { file, doc } = sl;
      const byId = new Map(doc.steps.map((s) => [s.id, s]));
      const pin = (id: string, check: (s: (typeof doc.steps)[0]) => string | null) => {
        const s = byId.get(id);
        if (!s) {
          warnings.push({ file, path: "steps", message: `soft-landing missing step "${id}" (beat sheet §0b)` });
          return;
        }
        const msg = check(s);
        if (msg) warnings.push({ file, path: `steps.${id}`, message: msg });
      };
      pin("return-dekkar", (s) => {
        if (s.objective.kind !== "deliver" || s.objective.consume !== false) {
          return `return-dekkar must be deliver with consume:false (show fish, keep for cook)`;
        }
        return null;
      });
      pin("return-logs", (s) => {
        if (s.objective.kind !== "deliver" || s.objective.consume !== false) {
          return `return-logs must be deliver with consume:false (keep logs for fire)`;
        }
        return null;
      });
      pin("return-cooked", (s) => {
        if (s.objective.kind !== "deliver") return `return-cooked must be deliver`;
        if (s.objective.consume === false) return `return-cooked must consume the cooked fish (omit consume or true)`;
        return null;
      });
      pin("light-fire", (s) => {
        if (s.objective.kind !== "custom" || s.objective.tag !== "light-fire") {
          return `light-fire must be custom tag "light-fire"`;
        }
        if (s.coach?.select?.type !== "kindling") {
          return `light-fire coach should select kindling (bag glow → Start fire)`;
        }
        return null;
      });
      pin("cook-fish", (s) => {
        if (!s.coach?.pointNearestFire) {
          return `cook-fish coach should use pointNearestFire (arrow over live campfire)`;
        }
        if (s.coach?.point) {
          return `cook-fish should not hardcode coach.point when using pointNearestFire`;
        }
        return null;
      });
      // Gift scrap wood after keep-logs is a known first-try footgun.
      for (const { file: nfile, doc: npc } of npcs) {
        if (npc.id !== "dekkar" || !npc.talk) continue;
        for (const node of npc.talk.nodes) {
          for (const c of node.choices ?? []) {
            if (c.action?.kind === "giveItem" && c.action.type === "oak-logs") {
              warnings.push({
                file: nfile,
                path: `talk.nodes.${node.id}`,
                message: `dekkar giveItem oak-logs — soft-landing keeps player logs for fire; gift kindling only (quest-authoring §0b/§0c)`,
              });
            }
          }
        }
      }
    }
  }

  // -- decos ------------------------------------------------------------------
  type DecoDoc = import("./schemas.ts").DecoDoc;
  const decos = structural<DecoDoc>(raw.decos, "deco", errors);
  const seenDecoTypes = new Map<string, string>();
  for (const { file, doc } of decos) {
    const prev = seenDecoTypes.get(doc.type);
    if (prev) errors.push({ file, path: "type", message: `duplicate deco type "${doc.type}" (also in ${prev})` });
    else seenDecoTypes.set(doc.type, file);
    if (!knownModels.has(doc.model)) {
      errors.push({ file, path: "model", message: `unknown model "${doc.model}"${suggest(doc.model, knownModels)}` });
    }
    // Interact block: kind-specific required fields + item-type cross-refs.
    const it = doc.interact;
    if (it) {
      if (it.kind === "shake" && !it.dropItem) {
        errors.push({ file, path: "interact.dropItem", message: `kind "shake" requires dropItem` });
      }
      if (it.kind === "pick" && !it.item) {
        errors.push({ file, path: "interact.item", message: `kind "pick" requires item` });
      }
      if (it.kind === "chop" && !it.logs) {
        errors.push({ file, path: "interact.logs", message: `kind "chop" requires logs (item type granted on success)` });
      }
      if (it.kind === "open" && !it.loot) {
        warnings.push({ file, path: "interact.loot", message: `kind "open" with no loot — the chest opens empty` });
      }
      if (it.kind === "use" && (it.loot || it.dropItem || it.item || it.logs)) {
        warnings.push({ file, path: "interact.kind", message: `kind "use" is a client-only station verb — loot/dropItem/item/logs are ignored` });
      }
      for (const [path, type] of [["interact.dropItem", it.dropItem], ["interact.item", it.item], ["interact.logs", it.logs]] as const) {
        if (type && !itemTypes.has(type)) {
          errors.push({ file, path, message: `unknown item type "${type}"${suggest(type, itemTypes)}` });
        }
      }
      (it.loot ?? []).forEach((l, i) => {
        if (!itemTypes.has(l.type)) {
          errors.push({ file, path: `interact.loot[${i}].type`, message: `unknown item type "${l.type}"${suggest(l.type, itemTypes)}` });
        }
      });
      // An "open" prop swaps to its open build — the coded registry must have it.
      if (it.kind === "open" && !CODED_MODELS[`${doc.model}-open`]) {
        errors.push({ file, path: "interact.kind", message: `kind "open" needs a coded "${doc.model}-open" model (the opened build) in src/model/coded.ts` });
      }
    }
  }

  // -- scatter palettes -------------------------------------------------------
  // Reusable named scatter compositions; maps reference one by id. Needs
  // seenDecoTypes (built just above), so it lives after the decos block.
  type ScatterPaletteDoc = import("./schemas.ts").ScatterPaletteDoc;
  const scatterPalettes = structural<ScatterPaletteDoc>(raw["scatter-palettes"], "scatter-palette", errors);
  const scatterPaletteIds = new Map<string, string>();
  for (const { file, doc } of scatterPalettes) {
    const prev = scatterPaletteIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate scatter-palette id "${doc.id}" (also in ${prev})` });
    else scatterPaletteIds.set(doc.id, file);
    doc.entries.forEach((e, i) => {
      if (!seenDecoTypes.has(e.deco)) {
        errors.push({ file, path: `entries[${i}].deco`, message: `unknown deco type "${e.deco}"${suggest(e.deco, seenDecoTypes.keys())}` });
      }
    });
  }

  // -- shops ------------------------------------------------------------------
  type ShopDoc = import("./schemas.ts").ShopDoc;
  const shops = structural<ShopDoc>(raw.shops, "shop", errors);
  const seenShopIds = new Map<string, string>();
  for (const { file, doc } of shops) {
    const prev = seenShopIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate shop id "${doc.id}" (also in ${prev})` });
    else seenShopIds.set(doc.id, file);

    if (!npcIds.has(doc.npc)) {
      errors.push({ file, path: "npc", message: `unknown NPC "${doc.npc}"${suggest(doc.npc, npcIds)}` });
    }
    const seenWares = new Set<string>();
    doc.wares.forEach((w, i) => {
      if (seenWares.has(w.type)) errors.push({ file, path: `wares[${i}].type`, message: `duplicate ware "${w.type}"` });
      seenWares.add(w.type);
      if (!itemTypes.has(w.type)) {
        errors.push({ file, path: `wares[${i}].type`, message: `unknown item type "${w.type}"${suggest(w.type, itemTypes)}` });
      }
      if (w.type === "obol") errors.push({ file, path: `wares[${i}].type`, message: `obol is currency, not a ware` });
      if (w.sell > w.buy) {
        warnings.push({ file, path: `wares[${i}].sell`, message: `sell (${w.sell}) exceeds buy (${w.buy}) — players could farm obol` });
      }
    });
  }

  // openShop actions (collected in the npc pass) → a real shop id. A typo here
  // otherwise validates clean and silently no-ops in game (shop.ts openShop bails on
  // an unknown id), unlike every other dangling reference which gets a did-you-mean.
  for (const { file, path, id } of pendingOpenShops) {
    if (!seenShopIds.has(id)) {
      errors.push({ file, path, message: `unknown shop "${id}"${suggest(id, seenShopIds.keys())}` });
    }
  }

  // -- standing (title ladders) ----------------------------------------------
  type StandingDoc = import("./schemas.ts").StandingDoc;
  const standings = structural<StandingDoc>(raw.standing, "standing", errors);
  const standingIds = new Map<string, string>();
  for (const { file, doc } of standings) {
    const prev = standingIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate standing id "${doc.id}" (also in ${prev})` });
    else standingIds.set(doc.id, file);

    if (!npcIds.has(doc.patron)) {
      errors.push({ file, path: "patron", message: `unknown NPC "${doc.patron}"${suggest(doc.patron, npcIds)}` });
    }
    // Ranks must ascend by their progress threshold, else a promotion could skip.
    doc.ranks.forEach((r, i) => {
      if (i > 0 && r.progress < doc.ranks[i - 1]!.progress) {
        errors.push({ file, path: `ranks[${i}].progress`, message: `progress must ascend (rank ${i} < rank ${i - 1})` });
      }
    });
  }

  // -- skills (box trees) -----------------------------------------------------
  type SkillDoc = import("./schemas.ts").SkillDoc;
  const skills = structural<SkillDoc>(raw.skills, "skill", errors);
  const skillIds = new Map<string, string>();
  const allBoxIds = new Set<string>(); // across every skill — move/weapon requiresBox refs
  for (const { file, doc } of skills) {
    const prev = skillIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate skill id "${doc.id}" (also in ${prev})` });
    else skillIds.set(doc.id, file);

    if (!npcIds.has(doc.patron)) {
      errors.push({ file, path: "patron", message: `unknown NPC "${doc.patron}"${suggest(doc.patron, npcIds)}` });
    }
    if (!standingIds.has(doc.track)) {
      errors.push({ file, path: "track", message: `unknown standing track "${doc.track}"${suggest(doc.track, standingIds.keys())}` });
    }
    const barSet = new Set(doc.xpBars);
    const boxIds = new Set(doc.boxes.map((b) => b.id));
    for (const b of boxIds) allBoxIds.add(b);
    // rungNames[bar] must align 1:1 with thresholds[bar] when both are set.
    if (doc.rungNames) {
      for (const [bar, names] of Object.entries(doc.rungNames)) {
        const thr = doc.thresholds?.[bar];
        if (!thr) {
          errors.push({
            file, path: `rungNames.${bar}`,
            message: `rungNames for "${bar}" has no matching thresholds entry`,
          });
        } else if (names.length !== thr.length) {
          errors.push({
            file, path: `rungNames.${bar}`,
            message: `rungNames.${bar} has ${names.length} name(s) but thresholds.${bar} has ${thr.length} rung(s)`,
          });
        }
      }
    }
    const seenBoxIds = new Set<string>();
    doc.boxes.forEach((b, i) => {
      if (seenBoxIds.has(b.id)) errors.push({ file, path: `boxes[${i}].id`, message: `duplicate box id "${b.id}"` });
      seenBoxIds.add(b.id);
      if (!barSet.has(b.requires.bar)) {
        errors.push({ file, path: `boxes[${i}].requires.bar`, message: `unknown xp bar "${b.requires.bar}"${suggest(b.requires.bar, barSet)}` });
      }
      b.requires.prereq?.forEach((p, j) => {
        if (!boxIds.has(p)) errors.push({ file, path: `boxes[${i}].requires.prereq[${j}]`, message: `unknown box "${p}"${suggest(p, boxIds)}` });
        if (p === b.id) errors.push({ file, path: `boxes[${i}].requires.prereq[${j}]`, message: `box "${b.id}" cannot require itself` });
      });
      const rk = b.requires.rank;
      if (rk && !standingIds.has(rk.track)) {
        errors.push({ file, path: `boxes[${i}].requires.rank.track`, message: `unknown standing track "${rk.track}"${suggest(rk.track, standingIds.keys())}` });
      }
      if (b.grants.weaponType && !itemTypes.has(b.grants.weaponType)) {
        // weaponType names an item type gated behind the box (sample content — may
        // reference a not-yet-authored type; warn rather than block).
        warnings.push({ file, path: `boxes[${i}].grants.weaponType`, message: `unknown item type "${b.grants.weaponType}"${suggest(b.grants.weaponType, itemTypes)}` });
      }
      // grants.move is closed after the moves pass (need the move-id set).
    });
  }

  // -- moves (special combat moves, plan 14) ------------------------------------
  // Parsed after skills so a move's requiresBox resolves against the full box-id
  // set; weapon.moves refs (collected in the items pass) close here.
  type MoveDoc = import("./schemas.ts").MoveDoc;
  const moveDocs = structural<MoveDoc>(raw.moves, "move", errors);
  const seenMoveIds = new Map<string, string>();
  const moveRequiresBox = new Map<string, string>(); // move id → requiresBox
  for (const { file, doc } of moveDocs) {
    const prev = seenMoveIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate move id "${doc.id}" (also in ${prev})` });
    else seenMoveIds.set(doc.id, file);

    if (doc.requiresBox !== undefined && !allBoxIds.has(doc.requiresBox)) {
      errors.push({ file, path: "requiresBox", message: `unknown skill box "${doc.requiresBox}"${suggest(doc.requiresBox, allBoxIds)}` });
    }
    if (doc.requiresBox) moveRequiresBox.set(doc.id, doc.requiresBox);
  }
  for (const { file, path, id } of pendingWeaponMoves) {
    if (!seenMoveIds.has(id)) {
      errors.push({ file, path, message: `unknown move "${id}"${suggest(id, seenMoveIds.keys())}` });
    }
  }
  // grants.move ↔ move.requiresBox: journal chip must name a real move, and the
  // move's gate should point back at this box (authoritative unlock rule).
  for (const { file, doc } of skills) {
    doc.boxes.forEach((b, i) => {
      const mid = b.grants.move;
      if (!mid) return;
      if (!seenMoveIds.has(mid)) {
        errors.push({
          file, path: `boxes[${i}].grants.move`,
          message: `unknown move "${mid}"${suggest(mid, seenMoveIds.keys())}`,
        });
        return;
      }
      const gate = moveRequiresBox.get(mid);
      if (gate !== b.id) {
        errors.push({
          file, path: `boxes[${i}].grants.move`,
          message: `move "${mid}" requiresBox is ${gate ? `"${gate}"` : "unset"} — must be "${b.id}"`,
        });
      }
    });
  }
  // An item's gate boxes (weapon / rod / net / shield requiresBox) should be real skill
  // boxes too (warn, not error — sample content may reference a not-yet-authored
  // box; moves' requiresBox errors above because moves shipped after skills).
  for (const { file, doc } of items) {
    for (const [path, rb] of [
      ["weapon.requiresBox", doc.weapon?.requiresBox],
      ["shield.requiresBox", doc.shield?.requiresBox],
      ["fishing.requiresBox", doc.fishing?.requiresBox],
      ["net.requiresBox", doc.net?.requiresBox],
      ["hunting.requiresBox", doc.hunting?.requiresBox],
      ["trap.requiresBox", doc.trap?.requiresBox],
      ["brew.requiresBox", doc.brew?.requiresBox],
    ] as const) {
      if (rb !== undefined && allBoxIds.size && !allBoxIds.has(rb)) {
        warnings.push({ file, path, message: `unknown skill box "${rb}"${suggest(rb, allBoxIds)}` });
      }
    }
    // A trap's / fermenter's haul rolls item types (plans 11/18) — every entry
    // must be a real item.
    for (const [path, haul] of [["trap.haul", doc.trap?.haul], ["brew.haul", doc.brew?.haul]] as const) {
      (haul ?? []).forEach((l, i) => {
        if (!itemTypes.has(l.type)) {
          errors.push({ file, path: `${path}[${i}].type`, message: `unknown item type "${l.type}"${suggest(l.type, itemTypes)}` });
        }
        if (l.min !== undefined && l.max !== undefined && l.min > l.max) {
          errors.push({ file, path: `${path}[${i}]`, message: `min (${l.min}) > max (${l.max})` });
        }
      });
    }
  }

  // -- catch tables (fishing, plan 10) -------------------------------------------
  // Weighted fish tables: unique ids, every entry a real item type. Inbound refs
  // are CODE-side in CP1 (index.ts constructs the ids), so the runtime's required
  // set is declared in src/sim/fishing.ts and asserted here — a table rename
  // can't silently fall back to tier 0 in production.
  type CatchTableDoc = import("./schemas.ts").CatchTableDoc;
  const catchTables = structural<CatchTableDoc>(raw["catch-tables"], "catch-table", errors);
  const seenTableIds = new Map<string, string>();
  for (const { file, doc } of catchTables) {
    const prev = seenTableIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate catch-table id "${doc.id}" (also in ${prev})` });
    else seenTableIds.set(doc.id, file);

    doc.entries.forEach((en, i) => {
      if (!itemTypes.has(en.type)) {
        errors.push({ file, path: `entries[${i}].type`, message: `unknown item type "${en.type}"${suggest(en.type, itemTypes)}` });
      }
    });
  }
  if (catchTables.length) {
    for (const id of REQUIRED_CATCH_TABLES) {
      if (!seenTableIds.has(id)) {
        errors.push({ file: "content/catch-tables", path: id, message: `the game references catch-table "${id}" by id (REQUIRED_CATCH_TABLES, src/sim/fishing.ts) but no doc defines it${suggest(id, seenTableIds.keys())}` });
      }
    }
  }

  // -- tunables (per-skill pacing docs) -----------------------------------------
  // One doc per skill; fields override the sim defaults via src/tunables.ts.
  // Checks: unique ids, sane ranges the schema can't express (min ≤ max), and
  // anglingXp keyed by real item types.
  type TunablesDoc = import("./schemas.ts").TunablesDoc;
  const tunables = structural<TunablesDoc>(raw.tunables, "tunables", errors);
  const seenTunableIds = new Map<string, string>();
  for (const { file, doc } of tunables) {
    const prev = seenTunableIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate tunables id "${doc.id}" (also in ${prev})` });
    else seenTunableIds.set(doc.id, file);

    // Living Waters encounter/minigame pairs — min ≤ max where both are set.
    for (const [lo, hi] of [
      ["investigateMin", "investigateMax"],
      ["nibbleMin", "nibbleMax"],
      ["nibbleGapMin", "nibbleGapMax"],
    ] as const) {
      const a = doc[lo], b = doc[hi];
      if (a !== undefined && b !== undefined && a > b) {
        errors.push({ file, path: lo, message: `${lo} (${a}) exceeds ${hi} (${b})` });
      }
    }
    if (doc.netCapacityMin !== undefined && doc.netCapacityMax !== undefined && doc.netCapacityMin > doc.netCapacityMax) {
      errors.push({ file, path: "netCapacityMin", message: `netCapacityMin (${doc.netCapacityMin}) exceeds netCapacityMax (${doc.netCapacityMax})` });
    }
    for (const type of Object.keys(doc.anglingXp ?? {})) {
      if (!itemTypes.has(type)) {
        errors.push({ file, path: `anglingXp.${type}`, message: `unknown item type "${type}"${suggest(type, itemTypes)}` });
      }
    }
    if (doc.trapCapacityMin !== undefined && doc.trapCapacityMax !== undefined && doc.trapCapacityMin > doc.trapCapacityMax) {
      errors.push({ file, path: "trapCapacityMin", message: `trapCapacityMin (${doc.trapCapacityMin}) exceeds trapCapacityMax (${doc.trapCapacityMax})` });
    }
    // stalkingXp is keyed by prey KIND (npc doc id), not item type.
    const npcKindIds = new Set(npcs.map((n) => n.doc.id));
    for (const kind of Object.keys(doc.stalkingXp ?? {})) {
      if (!npcKindIds.has(kind)) {
        errors.push({ file, path: `stalkingXp.${kind}`, message: `unknown NPC kind "${kind}"${suggest(kind, npcKindIds)}` });
      }
    }
    // cooking (plans 18/25): cookTier + cookXp are keyed by raw-ingredient item type.
    if (doc.brewCapacityMin !== undefined && doc.brewCapacityMax !== undefined && doc.brewCapacityMin > doc.brewCapacityMax) {
      errors.push({ file, path: "brewCapacityMin", message: `brewCapacityMin (${doc.brewCapacityMin}) exceeds brewCapacityMax (${doc.brewCapacityMax})` });
    }
    for (const key of ["cookTier", "cookXp"] as const) {
      for (const type of Object.keys(doc[key] ?? {})) {
        if (!itemTypes.has(type)) {
          errors.push({ file, path: `${key}.${type}`, message: `unknown item type "${type}"${suggest(type, itemTypes)}` });
        }
      }
    }
  }

  // -- scenarios (Skill Sim presets) -------------------------------------------
  // Spawns reference NPC DOC ids (kinds, not spawn ids — the arena mints fresh
  // instances); faction overrides resolve like NPC faction tags. Scenario ids
  // are studio conveniences, not persistence keys.
  type ScenarioDoc = import("./schemas.ts").ScenarioDoc;
  const scenarios = structural<ScenarioDoc>(raw.scenarios, "scenario", errors);
  const seenScenarioIds = new Map<string, string>();
  for (const { file, doc } of scenarios) {
    const prev = seenScenarioIds.get(doc.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate scenario id "${doc.id}" (also in ${prev})` });
    else seenScenarioIds.set(doc.id, file);

    doc.spawns.forEach((sp, i) => {
      const npcDoc = npcs.find((n) => n.doc.id === sp.npc)?.doc;
      if (!npcDoc) {
        errors.push({ file, path: `spawns[${i}].npc`, message: `unknown NPC doc "${sp.npc}" (a kind id, not a spawn id)${suggest(sp.npc, npcs.map((n) => n.doc.id))}` });
      } else if (!npcDoc.combat) {
        warnings.push({ file, path: `spawns[${i}].npc`, message: `"${sp.npc}" has no combat block — it will just stand in the arena` });
      }
      if (sp.faction !== undefined && haveFactionsDoc && !factionIds.has(sp.faction)) {
        errors.push({ file, path: `spawns[${i}].faction`, message: `unknown faction "${sp.faction}"${suggest(sp.faction, factionIds)}` });
      }
    });
  }

  // -- structure documents (data-driven buildings) -------------------------------
  type StructureDoc = import("./schemas.ts").StructureDoc;
  const structureDocs = structural<StructureDoc>(raw.structures, "structure", errors);
  const structureIds = new Map<string, string>();
  const structureById = new Map<string, StructureDoc>();
  for (const { file, doc: authored } of structureDocs) {
    const prev = structureIds.get(authored.id);
    if (prev) errors.push({ file, path: "id", message: `duplicate structure id "${authored.id}" (also in ${prev})` });
    else {
      structureIds.set(authored.id, file);
      structureById.set(authored.id, authored);
    }

    // Resolve param expressions FIRST (with the declared defaults) — the
    // geometric lints below judge the numbers the game will actually build.
    let doc: StructureDoc;
    try {
      doc = resolveStructureDoc(authored);
    } catch (e) {
      errors.push({ file, path: "ops", message: (e as Error).message });
      continue;
    }

    // Variations: each param key must be declared, and the doc must resolve
    // cleanly under that variant's params (this is what parse-checks the `when`
    // guards + expressions along each variation's branch).
    {
      const declared = Object.keys(authored.params ?? {});
      for (const [vName, v] of Object.entries(authored.variants ?? {})) {
        const badKeys = Object.keys(v.params ?? {}).filter((k) => !declared.includes(k));
        for (const k of badKeys) {
          errors.push({ file, path: `variants.${vName}.params.${k}`, message: `structure "${authored.id}" declares no param "${k}" (declared: ${declared.join(", ") || "none"})${suggest(k, declared)}` });
        }
        // Only attempt the resolve when keys are sound — otherwise it re-throws the
        // same "undeclared param" for the whole variant, double-reporting one typo.
        if (badKeys.length === 0) {
          try {
            resolveStructureDoc(authored, v.params as Record<string, number>);
          } catch (e) {
            errors.push({ file, path: `variants.${vName}`, message: (e as Error).message });
          }
        }
        if (v.palette) {
          try {
            resolvePalette({ ...authored.palette, ...v.palette });
          } catch (e) {
            errors.push({ file, path: `variants.${vName}.palette`, message: (e as Error).message });
          }
        }
      }
    }

    let palette: Record<string, number> = {};
    try {
      palette = resolvePalette(doc.palette ?? {});
    } catch (e) {
      errors.push({ file, path: "palette", message: (e as Error).message });
      continue;
    }
    const checkColor = (c: string | undefined, path: string) => {
      if (c === undefined) return;
      try {
        colorOf(c, palette);
      } catch {
        errors.push({ file, path, message: `"${c}" is neither a palette key nor #rrggbb${suggest(c, Object.keys(palette))}` });
      }
    };

    // Per-op lints — the structure playbook (structure/CLAUDE.md), encoded.
    const floorYs = new Set<number>([0]);
    for (const op of doc.ops) {
      if (op.op === "room") floorYs.add(op.baseY);
      if (op.op === "floor") floorYs.add(op.surfaceY ?? op.y);
    }
    let topFloor = 0;
    for (const y of floorYs) topFloor = Math.max(topFloor, y);

    doc.ops.forEach((op, i) => {
      const at = `ops[${i}]`;
      // Colors on every op that has them.
      if ("color" in op) checkColor(op.color, `${at}.color`);
      if (op.op === "room") { checkColor(op.wallColor, `${at}.wallColor`); checkColor(op.floorColor, `${at}.floorColor`); checkColor(op.logColor, `${at}.logColor`); }
      if (op.op === "tower") checkColor(op.roofColor, `${at}.roofColor`);
      if (op.op === "gableRoof") { checkColor(op.colorDk, `${at}.colorDk`); checkColor(op.ridgeColor, `${at}.ridgeColor`); }
      if (op.op === "window") { checkColor(op.frame, `${at}.frame`); checkColor(op.glass, `${at}.glass`); }
      if (op.op === "dot") checkColor(op.color, `${at}.color`);
      if (op.op === "part") {
        op.parts.forEach((p, j) => checkColor(p.color, `${at}.parts[${j}].color`));
      }

      if (op.op === "room") {
        if (op.minX >= op.maxX || op.minZ >= op.maxZ) {
          errors.push({ file, path: at, message: "room min must be < max on both axes" });
        }
        const bySide = new Map<string, number>();
        (op.openings ?? []).forEach((o, j) => {
          if (o.kind === "door") bySide.set(o.side, (bySide.get(o.side) ?? 0) + 1);
          // The opening must fit inside its wall's run.
          const [lo, hi] = o.side === "north" || o.side === "south" ? [op.minX, op.maxX] : [op.minZ, op.maxZ];
          const half = (o.w ?? (o.kind === "door" ? 1.4 : 1.5)) / 2;
          if (o.at - half < lo + op.wallT || o.at + half > hi - op.wallT) {
            errors.push({ file, path: `${at}.openings[${j}].at`, message: `opening (${o.at}±${half}) exceeds the ${o.side} wall's run (${lo + op.wallT}..${hi - op.wallT})` });
          }
        });
        for (const [side, n] of bySide) {
          if (n > 1) errors.push({ file, path: `${at}.openings`, message: `${n} doors on the ${side} side — v1 supports one door per side` });
        }
        if (op.baseY === 0 && !(op.openings ?? []).some((o) => o.kind === "door")) {
          warnings.push({ file, path: at, message: "ground-floor room has no door — nothing can walk in" });
        }
      }
      if (op.op === "staircase") {
        if (op.yHigh <= op.yLow) {
          errors.push({ file, path: at, message: `yHigh (${op.yHigh}) must exceed yLow (${op.yLow})` });
        } else if (![...floorYs].some((y) => Math.abs(y - op.yHigh) < 0.15)) {
          warnings.push({ file, path: at, message: `staircase tops out at ${op.yHigh}, but no floor sits there (floors: ${[...floorYs].join(", ")})` });
        }
      }
      if (op.op === "ceiling" && op.y <= topFloor) {
        warnings.push({ file, path: at, message: `ceiling at ${op.y} is not above the top floor (${topFloor})` });
      }
      if (op.op === "furnish") {
        if (!(FURNITURE_PIECES as readonly string[]).includes(op.piece)) {
          errors.push({ file, path: `${at}.piece`, message: `unknown furniture "${op.piece}" (have: ${FURNITURE_PIECES.join(", ")})${suggest(op.piece, FURNITURE_PIECES)}` });
        }
      }
    });
  }

  // -- maps (content/maps/*.json, plan 20 + 21) ----------------------------------
  // One doc = one buildable world: terrain layers + every placement (including
  // NPC spawns since plan 21). The placement cross-refs moved here from the old
  // content/world files; new spatial sanity (bounds / feature magnitude / brush
  // guardrails) is validated per map. Underwater checks are deliberately
  // EDITOR-side (sampling composed terrain from inside validation of the
  // terrain being validated is circular). `mapDocs` parsed at the top (the
  // quest pass needs spawn ids).
  const seenMapIds = new Map<string, string>();
  const seenSpawnIds = new Map<string, string>(); // spawn id → file, ACROSS maps (flat npc_state namespace)
  const defaultMaps: string[] = [];
  for (const { file, doc } of mapDocs) {
    const prevMap = seenMapIds.get(doc.id);
    if (prevMap) errors.push({ file, path: "id", message: `duplicate map id "${doc.id}" (also in ${prevMap})` });
    seenMapIds.set(doc.id, file);

    const half = doc.size / 2;
    const boundErr = (x: number, z: number, path: string, what: string) => {
      if (Math.abs(x) > half || Math.abs(z) > half) {
        errors.push({ file, path, message: `${what} at (${x}, ${z}) is outside the map (±${half})` });
      }
    };

    if (doc.default) defaultMaps.push(doc.id);
    if (doc.defaultSpawn) boundErr(doc.defaultSpawn.x, doc.defaultSpawn.z, "defaultSpawn", "default spawn");
    if (doc.introSpawn) boundErr(doc.introSpawn.x, doc.introSpawn.z, "introSpawn", "intro spawn");

    // terrain features — anchors in bounds, magnitudes sane (the noise base
    // spans roughly ±8; a 40u spike is almost certainly a typo).
    doc.terrain.features.forEach((f, i) => {
      const at = `terrain.features[${i}]`;
      if (f.kind === "ridge") {
        f.points.forEach(([px, pz], j) => boundErr(px, pz, `${at}.points[${j}]`, "ridge point"));
      } else if (f.kind === "ramp") {
        boundErr(f.x0, f.z0, at, "ramp start");
        boundErr(f.x1, f.z1, at, "ramp end");
      } else {
        boundErr(f.x, f.z, at, `${f.kind} center`);
      }
      const mag =
        f.kind === "hill" || f.kind === "ridge" ? Math.abs(f.h)
        : f.kind === "basin" ? f.depth
        : f.kind === "ramp" ? Math.max(Math.abs(f.h0), Math.abs(f.h1))
        : 0;
      if (mag > 40) {
        warnings.push({ file, path: at, message: `feature magnitude ${mag} is extreme (the noise base spans ~±8)` });
      }
    });
    if (doc.terrain.features.length > 128) {
      warnings.push({ file, path: "terrain.features", message: `${doc.terrain.features.length} features — heightAt scans them linearly per sample; consider consolidating` });
    }
    doc.terrain.flats.forEach((f, i) => boundErr(f.x, f.z, `terrain.flats[${i}]`, "flat zone"));

    // brush — cell keys inside the map, deltas sane, doc-size guardrails.
    if (doc.terrain.brush) {
      const { res, cells } = doc.terrain.brush;
      const keys = Object.keys(cells);
      if (keys.length > 150_000) {
        errors.push({ file, path: "terrain.brush.cells", message: `${keys.length} brush cells — over the 150k hard cap` });
      } else {
        if (keys.length > 25_000) {
          warnings.push({ file, path: "terrain.brush.cells", message: `${keys.length} brush cells — the doc is getting heavy (soft cap 25k)` });
        }
        let oob = 0, extreme = 0;
        for (const key of keys) {
          const c = key.indexOf(",");
          const ix = parseInt(key.slice(0, c), 10), iz = parseInt(key.slice(c + 1), 10);
          if (Math.abs(ix * res) > half || Math.abs(iz * res) > half) oob++;
          if (Math.abs(cells[key]!) > 25) extreme++;
        }
        if (oob) errors.push({ file, path: "terrain.brush.cells", message: `${oob} brush cell${oob === 1 ? "" : "s"} outside the map (±${half})` });
        if (extreme) warnings.push({ file, path: "terrain.brush.cells", message: `${extreme} brush cell${extreme === 1 ? "" : "s"} with |delta| > 25` });
      }
    }

    // scatter masks — bias needs a real deco type; rects need min < max.
    (doc.scatterMasks ?? []).forEach((m, i) => {
      const at = `scatterMasks[${i}]`;
      if (m.effect === "bias") {
        if (!m.deco) {
          errors.push({ file, path: `${at}.deco`, message: `bias mask needs a target deco type` });
        } else if (!seenDecoTypes.has(m.deco)) {
          errors.push({ file, path: `${at}.deco`, message: `unknown deco type "${m.deco}"${suggest(m.deco, seenDecoTypes.keys())}` });
        }
      }
      if (m.shape === "circle") {
        boundErr(m.x, m.z, at, "mask center");
      } else if (m.minX >= m.maxX || m.minZ >= m.maxZ) {
        errors.push({ file, path: at, message: `rect mask min must be < max` });
      }
    });

    // scatter palette — the map references a scatter-palettes doc by id.
    if (doc.scatterPalette && !scatterPaletteIds.has(doc.scatterPalette)) {
      errors.push({ file, path: "scatterPalette", message: `unknown scatter-palette "${doc.scatterPalette}"${suggest(doc.scatterPalette, scatterPaletteIds.keys())}` });
    }

    // authored decos (the old world/props — ids are prop_state persistence keys).
    // An `interact` block is no longer required: plain landmark decos are legit.
    const seenDecoIds = new Set<string>();
    doc.decos.forEach((p, i) => {
      if (seenDecoIds.has(p.id)) {
        errors.push({ file, path: `decos[${i}].id`, message: `duplicate deco id "${p.id}"` });
      }
      seenDecoIds.add(p.id);
      if (!seenDecoTypes.has(p.type)) {
        errors.push({ file, path: `decos[${i}].type`, message: `unknown deco type "${p.type}"${suggest(p.type, seenDecoTypes.keys())}` });
      }
      boundErr(p.x, p.z, `decos[${i}]`, `deco "${p.id}"`);
    });

    // seeded ground items.
    const seenItemIds = new Set<string>();
    doc.items.forEach((it, i) => {
      if (seenItemIds.has(it.id)) {
        errors.push({ file, path: `items[${i}].id`, message: `duplicate world item id "${it.id}"` });
      }
      seenItemIds.add(it.id);
      if (!itemTypes.has(it.type)) {
        errors.push({ file, path: `items[${i}].type`, message: `unknown item type "${it.type}"${suggest(it.type, itemTypes)}` });
      }
      boundErr(it.x, it.z, `items[${i}]`, `item "${it.id}"`);
    });

    // NPC spawns (plan 21 — placement moved here from the npc docs). Spawn ids
    // are the npc_state persistence keys: unique across EVERY map (npc_state's
    // id namespace stays flat even with per-map scoping).
    doc.npcs.forEach((sp, i) => {
      const at = `npcs[${i}]`;
      const prevSpawn = seenSpawnIds.get(sp.id);
      if (prevSpawn) {
        errors.push({ file, path: `${at}.id`, message: `duplicate spawn id "${sp.id}" (also in ${prevSpawn}) — persisted npc_state is keyed by it` });
      } else seenSpawnIds.set(sp.id, file);
      if (!npcById.has(sp.npc)) {
        errors.push({ file, path: `${at}.npc`, message: `unknown npc doc "${sp.npc}" (a kind id, not a spawn id)${suggest(sp.npc, npcById.keys())}` });
      }
      boundErr(sp.x, sp.z, at, `spawn "${sp.id}"`);
      if (badRoamBox(sp.roam)) {
        errors.push({ file, path: `${at}.roam`, message: "roam box min must be < max on both axes" });
      }
      if (sp.pose && !BODY_POSES[sp.pose]) {
        errors.push({ file, path: `${at}.pose`, message: `unknown body pose "${sp.pose}"${suggest(sp.pose, Object.keys(BODY_POSES))} (content/anim/clips.json poses)` });
      } else if (sp.pose && npcById.has(sp.npc)) {
        // Body poses drive the humanoid rig joints; any other model renders the
        // spawn silently standing (driveGait only collects a rig for humanoid
        // archetypes) — reject the dead content loudly instead.
        let kind = npcById.get(sp.npc);
        for (let hops = 0; kind && !kind.model && kind.base && hops < 8; hops++) kind = npcById.get(kind.base);
        const arch = kind?.model ? resolvedModels.get(kind.model)?.archetype : undefined;
        if (arch !== "humanoid") {
          errors.push({ file, path: `${at}.pose`, message: `pose "${sp.pose}" needs a humanoid-rigged model — npc "${sp.npc}"'s model ${kind?.model ? `"${kind.model}" is archetype "${arch ?? "parts"}"` : "is unset"}, so the pose would never render` });
        }
      }
    });

    // point portals (plan 24) — target resolves, own trigger + target spawn in
    // bounds, no self-portals. A portal whose effective arrival lands inside a
    // RETURN portal's trigger would ping-pong (the runtime cooldown makes it
    // slow, not fatal) — warn, don't error.
    const seenPortalIds = new Set<string>();
    (doc.portals ?? []).forEach((p, i) => {
      const at = `portals[${i}]`;
      if (p.id) {
        if (seenPortalIds.has(p.id)) {
          errors.push({ file, path: `${at}.id`, message: `duplicate portal id "${p.id}"` });
        }
        seenPortalIds.add(p.id);
      }
      boundErr(p.x, p.z, at, `portal${p.id ? ` "${p.id}"` : ""}`);
      if (p.r > 50) {
        warnings.push({ file, path: `${at}.r`, message: `portal radius ${p.r} is huge — a point portal is a doorway, not a zone` });
      }
      const target = mapDocById.get(p.to);
      if (p.to === doc.id) {
        errors.push({ file, path: `${at}.to`, message: `portal targets its own map` });
        return;
      }
      if (!target) {
        errors.push({ file, path: `${at}.to`, message: `unknown map "${p.to}"${suggest(p.to, mapDocById.keys())}` });
        return;
      }
      const targetHalf = target.size / 2;
      if (p.spawn && (Math.abs(p.spawn.x) > targetHalf || Math.abs(p.spawn.z) > targetHalf)) {
        errors.push({ file, path: `${at}.spawn`, message: `spawn (${p.spawn.x}, ${p.spawn.z}) is outside target map "${p.to}" (±${targetHalf})` });
      }
      const arrive = p.spawn ?? target.defaultSpawn ?? { x: 0, z: 0 };
      for (const q of target.portals ?? []) {
        if (q.to !== doc.id) continue;
        if (Math.hypot(arrive.x - q.x, arrive.z - q.z) <= q.r + 2) {
          warnings.push({ file, path: `${at}.spawn`, message: `arrival on "${p.to}" lands inside the return portal${q.id ? ` "${q.id}"` : ""}'s trigger — players will bounce back and forth` });
        }
      }
    });

    // fish population zones (Living Waters) — dup ids, bounds, and every
    // population entry must be a real item CARRYING a `fish` block (the block
    // is what makes it spawnable/catchable). Ids are authoring keys, not
    // persistence keys — no cross-map uniqueness needed. The "is the center
    // actually water" check lives in the studio Waters tool (terrain sampling
    // stays out of validate's import graph).
    const seenWaterIds = new Set<string>();
    const fishItemTypes = new Set(items.filter((i) => i.doc.fish).map((i) => i.doc.type));
    (doc.waters ?? []).forEach((w, i) => {
      const at = `waters[${i}]`;
      if (seenWaterIds.has(w.id)) {
        errors.push({ file, path: `${at}.id`, message: `duplicate water id "${w.id}"` });
      }
      seenWaterIds.add(w.id);
      boundErr(w.x, w.z, at, `water "${w.id}"`);
      w.population.forEach((p, j) => {
        if (!itemTypes.has(p.fish)) {
          errors.push({ file, path: `${at}.population[${j}].fish`, message: `unknown item type "${p.fish}"${suggest(p.fish, itemTypes)}` });
        } else if (items.length && !fishItemTypes.has(p.fish)) {
          errors.push({ file, path: `${at}.population[${j}].fish`, message: `"${p.fish}" is not a fish — its item doc needs a \`fish\` block to live in a water zone${suggest(p.fish, fishItemTypes)}` });
        }
      });
      const total = w.population.reduce((n, p) => n + p.count, 0);
      if (total > 40) {
        warnings.push({ file, path: `${at}.population`, message: `${total} fish in one zone — each is a simulated entity; consider splitting the water` });
      }
    });

    // ambient audio zones — dup ids, bounds, and the fade geometry must be
    // coherent (inner strictly inside the outer radius, else the crossfade
    // band is empty/inverted). Track EXISTENCE is deliberately unchecked:
    // tracks are plain assets/audio/*.{opus,mp3} files and validate.ts rides the
    // browser bundles (no fs) — the client console.warns on a 404 instead.
    const seenAudioIds = new Set<string>();
    (doc.audioZones ?? []).forEach((a, i) => {
      const at = `audioZones[${i}]`;
      if (seenAudioIds.has(a.id)) {
        errors.push({ file, path: `${at}.id`, message: `duplicate audio zone id "${a.id}"` });
      }
      seenAudioIds.add(a.id);
      boundErr(a.x, a.z, at, `audio zone "${a.id}"`);
      if (a.inner >= a.r) {
        errors.push({ file, path: `${at}.inner`, message: `inner full-volume radius ${a.inner} must be < the outer fade radius ${a.r}` });
      }
      if (a.r > doc.size) {
        warnings.push({ file, path: `${at}.r`, message: `audio zone radius ${a.r} exceeds the map size ${doc.size} — did you mean the map-wide \`ambience\` field?` });
      }
    });

    // edge links (plan 24) — whole-edge crossings need the neighbor to exist,
    // match in size (the along-edge coordinate transfers verbatim), and link
    // back reciprocally (A.east→B without B.west→A strands the return trip).
    for (const side of EDGE_SIDES) {
      const link = doc.edges?.[side];
      if (!link) continue;
      const at = `edges.${side}`;
      if (link.to === doc.id) {
        errors.push({ file, path: at, message: `edge links to its own map` });
        continue;
      }
      const target = mapDocById.get(link.to);
      if (!target) {
        errors.push({ file, path: at, message: `unknown map "${link.to}"${suggest(link.to, mapDocById.keys())}` });
        continue;
      }
      if (target.size !== doc.size) {
        errors.push({ file, path: at, message: `edge-linked maps must be the same size — "${doc.id}" is ${doc.size}, "${link.to}" is ${target.size} (the along-edge coordinate transfers verbatim)` });
      }
      if (target.edges?.[OPPOSITE_SIDE[side]]?.to !== doc.id) {
        errors.push({ file, path: at, message: `"${link.to}" does not link back (needs edges.${OPPOSITE_SIDE[side]}.to = "${doc.id}") — edge crossings must be reciprocal` });
      }
    }

    // roads — every waypoint inside the map.
    doc.roads.forEach((r, i) =>
      r.waypoints.forEach(([wx, wz], j) => boundErr(wx, wz, `roads[${i}].waypoints[${j}]`, "road waypoint")),
    );

    // structure placements — spec/variant/params cross-refs (moved verbatim
    // from the old world/structures.json pass) + bounds.
    const knownSpecs = new Set(["castle", ...structureIds.keys()]);
    doc.structures.forEach((p, i) => {
      boundErr(p.x, p.z, `structures[${i}]`, `structure "${p.spec}"`);
      if (!knownSpecs.has(p.spec)) {
        errors.push({ file, path: `structures[${i}].spec`, message: `unknown structure spec "${p.spec}"${suggest(p.spec, knownSpecs)}` });
        return;
      }

      // Explicit variant must be one the style doc declares (coded castle has none).
      let variantOverride: Record<string, number> = {};
      if (p.variant !== undefined) {
        if (p.spec === "castle") {
          errors.push({ file, path: `structures[${i}].variant`, message: `"${p.spec}" has no variants` });
        } else {
          const sdoc = structureById.get(p.spec);
          const names = Object.keys(sdoc?.variants ?? {});
          if (sdoc && !names.includes(p.variant)) {
            errors.push({ file, path: `structures[${i}].variant`, message: `structure "${p.spec}" has no variant "${p.variant}" (have: ${names.join(", ") || "none"})${suggest(p.variant, names)}` });
          } else if (sdoc) {
            variantOverride = sdoc.variants![p.variant]!.params ?? {};
          }
        }
      }

      if (!p.params) return;
      if (p.spec === "castle") {
        errors.push({ file, path: `structures[${i}].params`, message: `"castle" takes no params` });
      } else {
        // Structure-doc override: keys must be declared params, and the doc
        // must still resolve with the variant + placement params applied.
        const sdoc = structureById.get(p.spec);
        if (!sdoc) return; // duplicate-id docs already errored above
        const declared = Object.keys(sdoc.params ?? {});
        let keysOk = true;
        for (const k of Object.keys(p.params as Record<string, number>)) {
          if (!declared.includes(k)) {
            keysOk = false;
            errors.push({ file, path: `structures[${i}].params.${k}`, message: `structure "${p.spec}" declares no param "${k}" (declared: ${declared.join(", ") || "none"})${suggest(k, declared)}` });
          }
        }
        if (keysOk) {
          try {
            resolveStructureDoc(sdoc, { ...variantOverride, ...(p.params as Record<string, number>) });
          } catch (e) {
            errors.push({ file, path: `structures[${i}].params`, message: (e as Error).message });
          }
        }
      }
    });
  }

  // Exactly one map is the default (new/transferred/orphaned characters land
  // there — load.ts derives DEFAULT_MAP_ID from it).
  if (mapDocs.length) {
    if (defaultMaps.length === 0) {
      errors.push({ file: mapDocs[0]!.file, path: "default", message: `no map sets "default": true — exactly one must (the default map receives new characters)` });
    } else if (defaultMaps.length > 1) {
      errors.push({ file: mapDocs[0]!.file, path: "default", message: `${defaultMaps.length} maps set "default": true (${defaultMaps.join(", ")}) — exactly one may` });
    }
  }

  // -- cinematics ---------------------------------------------------------------
  // Shot lists (plan 26): the stage map + every actor/camera/action ref must
  // resolve, each shot must be able to END (duration or ≥1 blocking action —
  // actionBlocks() is the shared default table, imported from the runtime core
  // so the rule can't drift), path shots need an explicit duration, and at most
  // one doc may claim the firstLogin trigger. Gesture clip refs are deferred to
  // the anim pass below (moveClips keys live in the anim singleton).
  type CinematicDoc = import("./schemas.ts").CinematicDoc;
  const cinematics = structural<CinematicDoc>(raw.cinematics, "cinematic", errors);
  const cinematicIds = new Map<string, string>();
  const pendingGestureClips: { file: string; path: string; id: string }[] = [];
  {
    const npcDocIds = new Set(npcs.map((n) => n.doc.id));
    const poseNames = new Set(Object.keys(BODY_POSES));
    let firstLoginFile: string | null = null;
    for (const { file, doc } of cinematics) {
      const prev = cinematicIds.get(doc.id);
      if (prev) errors.push({ file, path: "id", message: `duplicate cinematic id "${doc.id}" (also in ${prev})` });
      else cinematicIds.set(doc.id, file);

      if (doc.trigger === "firstLogin") {
        if (firstLoginFile) {
          errors.push({ file, path: "trigger", message: `only one cinematic may claim trigger "firstLogin" (also in ${firstLoginFile})` });
        } else firstLoginFile = file;
      }

      if (!mapDocById.has(doc.map)) {
        errors.push({ file, path: "map", message: `unknown map "${doc.map}"${suggest(doc.map, mapDocById.keys())}` });
      }

      const actorIds = new Set<string>();
      doc.actors.forEach((a, i) => {
        if (actorIds.has(a.id)) errors.push({ file, path: `actors[${i}].id`, message: `duplicate actor id "${a.id}"` });
        actorIds.add(a.id);
        if (a.kind !== "player" && !npcDocIds.has(a.kind)) {
          errors.push({ file, path: `actors[${i}].kind`, message: `unknown NPC kind "${a.kind}" (or "player")${suggest(a.kind, npcDocIds)}` });
        }
        if (a.weapon) {
          if (!itemTypes.has(a.weapon)) {
            errors.push({ file, path: `actors[${i}].weapon`, message: `unknown item type "${a.weapon}"${suggest(a.weapon, itemTypes)}` });
          } else {
            const item = items.find((x) => x.doc.type === a.weapon);
            if (item && !item.doc.weapon) {
              errors.push({ file, path: `actors[${i}].weapon`, message: `"${a.weapon}" has no weapon block — actors hold equippables` });
            }
          }
        }
        if (a.pose && !poseNames.has(a.pose)) {
          errors.push({ file, path: `actors[${i}].pose`, message: `unknown body pose "${a.pose}"${suggest(a.pose, poseNames)} (content/anim/clips.json poses)` });
        }
      });
      // ride.of must name another actor in this doc (resolved after the full
      // actor set is known, since a rider may be listed before its vehicle).
      const ridden = new Set(doc.actors.filter((a) => a.ride).map((a) => a.id));
      doc.actors.forEach((a, i) => {
        if (a.ride) {
          if (!actorIds.has(a.ride.of)) {
            errors.push({ file, path: `actors[${i}].ride.of`, message: `unknown vehicle actor "${a.ride.of}"${suggest(a.ride.of, actorIds)}` });
          } else if (a.ride.of === a.id) {
            errors.push({ file, path: `actors[${i}].ride.of`, message: `an actor can't ride itself` });
          } else if (ridden.has(a.ride.of)) {
            // The stage composes riders in a single pass (vehicles → riders), so a
            // rider-of-a-rider would be placed a frame behind and mis-seated.
            errors.push({ file, path: `actors[${i}].ride.of`, message: `vehicle "${a.ride.of}" is itself a rider — ride chains aren't supported (compose onto a non-riding actor)` });
          }
        }
      });

      /** Check an ActorRef (id / id[] / [x,z] literal) against the doc's actors. */
      const checkRef = (path: string, ref: unknown): void => {
        const ids =
          typeof ref === "string"
            ? [ref]
            : Array.isArray(ref) && typeof ref[0] === "string"
              ? (ref as string[])
              : [];
        for (const id of ids) {
          if (!actorIds.has(id)) errors.push({ file, path, message: `unknown actor "${id}"${suggest(id, actorIds)}` });
        }
      };

      doc.shots.forEach((s, i) => {
        const at = `shots[${i}]`;
        const cam = s.camera;
        if (cam.shot === "twoShot") {
          checkRef(`${at}.camera.a`, cam.a);
          checkRef(`${at}.camera.b`, cam.b);
        } else if (cam.shot === "overShoulder") {
          checkRef(`${at}.camera.from`, cam.from);
          checkRef(`${at}.camera.to`, cam.to);
        } else if (cam.shot === "path") {
          checkRef(`${at}.camera.lookAt`, cam.lookAt);
          if (s.duration === undefined) {
            errors.push({ file, path: `${at}.duration`, message: `a "path" camera needs an explicit shot duration (the spline plays at constant speed over it)` });
          }
        } else {
          checkRef(`${at}.camera.target`, cam.target);
        }

        let hasBlocking = false;
        (s.actions ?? []).forEach((a, j) => {
          const ap = `${at}.actions[${j}]`;
          if ("actor" in a && !actorIds.has(a.actor)) {
            errors.push({ file, path: `${ap}.actor`, message: `unknown actor "${a.actor}"${suggest(a.actor, actorIds)}` });
          }
          if (a.do === "face" && typeof a.toward === "string") checkRef(`${ap}.toward`, a.toward);
          if (a.do === "gesture" && a.clip !== "primary") {
            pendingGestureClips.push({ file, path: `${ap}.clip`, id: a.clip });
          }
          if (a.do === "pose" && !poseNames.has(a.to)) {
            errors.push({ file, path: `${ap}.to`, message: `unknown body pose "${a.to}"${suggest(a.to, poseNames)} (content/anim/clips.json poses)` });
          }
          if (actionBlocks(a)) hasBlocking = true;
        });
        if (s.duration === undefined && !hasBlocking) {
          errors.push({ file, path: at, message: "a shot needs `duration` or at least one blocking action (say/walk/gesture/wait/fade), else it can never end" });
        }
      });
    }
    // playCinematic talk actions (collected in the npcs pass) resolve here.
    for (const { file, path, id } of pendingPlayCinematics) {
      if (!cinematicIds.has(id)) {
        errors.push({ file, path, message: `unknown cinematic "${id}"${suggest(id, cinematicIds.keys())}` });
      }
    }
  }

  // -- anim clips ----------------------------------------------------------------
  // Per-ARCHETYPE animation: keys under `archetypes` must be WeaponArchetypeIds,
  // every weapon item must declare a known archetype that has an anim set
  // (missing ⇒ falls back to the unarmed jab — warn), and move clips referenced
  // by content/moves must exist in `moveClips`.
  // Also soft-check item grip/style against the archetype's expected defaults.
  {
    const weaponItems = items.filter((x) => x.doc.weapon);
    for (const { file, doc } of weaponItems) {
      // archetype/grip/style are schema-validated enums here (parse succeeded),
      // so the archetype is always a known id — just soft-check grip/style drift.
      const w = doc.weapon!;
      const meta = WEAPON_ARCHETYPES[w.archetype];
      if (w.grip !== meta.grip) {
        warnings.push({
          file,
          path: "weapon.grip",
          message: `grip "${w.grip}" doesn't match archetype ${w.archetype}'s expected "${meta.grip}" (animation is shared; grip is combat mechanics)`,
        });
      }
      if (w.style !== meta.style) {
        warnings.push({
          file,
          path: "weapon.style",
          message: `style "${w.style}" doesn't match archetype ${w.archetype}'s expected "${meta.style}" (animation is shared; style is combat mechanics)`,
        });
      }
    }
  }
  if (raw.anim) {
    const res = SCHEMAS["anim-clips"].safeParse(raw.anim.data);
    if (!res.success) {
      for (const issue of res.error.issues) {
        errors.push({ file: raw.anim.file, path: zodPath(issue.path), message: issue.message });
      }
    } else {
      const usedArchetypes = new Set<WeaponArchetypeId>();
      for (const { doc } of items) {
        if (doc.weapon) usedArchetypes.add(doc.weapon.archetype);
      }
      for (const id of Object.keys(res.data.archetypes)) {
        if (!isWeaponArchetypeId(id)) {
          errors.push({
            file: raw.anim.file,
            path: `archetypes.${id}`,
            message: `"${id}" is not a weapon archetype id${suggest(id, new Set(WEAPON_ARCHETYPE_IDS))}`,
          });
        }
      }
      for (const id of usedArchetypes) {
        if (!res.data.archetypes[id]) {
          warnings.push({
            file: raw.anim.file,
            path: `archetypes.${id}`,
            message: `archetype "${id}" is used by weapons but has no animation set — falls back to the unarmed jab with no carry/ready stance`,
          });
        }
      }
      for (const { file, doc } of moveDocs) {
        if (!res.data.moveClips[doc.clip]) {
          errors.push({
            file,
            path: "clip",
            message: `move clip "${doc.clip}" is not defined in anim moveClips${suggest(doc.clip, Object.keys(res.data.moveClips))}`,
          });
        }
      }
      // Cinematic gesture clips (deferred from the cinematics pass above) share
      // the same rule: "primary" aside, a gesture is a moveClips key.
      for (const { file, path, id } of pendingGestureClips) {
        if (!res.data.moveClips[id]) {
          errors.push({
            file,
            path,
            message: `gesture clip "${id}" is not "primary" or an anim moveClips key${suggest(id, Object.keys(res.data.moveClips))}`,
          });
        }
      }

      // When a clips hold override exists, re-check seating on the EFFECTIVE hold
      // (item + override) for every item of that archetype. Also warn on large
      // stance grip deltas — those re-seat the mesh and hide a bad rot.
      for (const [archId, wa] of Object.entries(res.data.archetypes)) {
        const ofArch = items.filter((i) => i.doc.weapon?.archetype === archId);
        for (const item of ofArch) {
          const baseHold = item.doc.weapon?.hold;
          if (baseHold && wa.hold) {
            const effective = mergeHold(baseHold, wa.hold);
            const mdoc = item.doc.model ? resolvedModels.get(item.doc.model) : undefined;
            const lm = mdoc?.landmarks as Landmarks | undefined;
            for (const issue of checkHoldSeat(effective, lm, {
              pathPrefix: `archetypes.${archId}.hold (via ${item.doc.type})`,
            })) {
              (issue.level === "error" ? errors : warnings).push({
                file: raw.anim.file,
                path: issue.path.startsWith("landmarks")
                  ? `archetypes.${archId} → ${item.doc.type} → ${issue.path}`
                  : issue.path,
                message: issue.message,
              });
            }
          }
          // Stance grip: check once per archetype using the first item's offset
          // as the "intentional large offset" gate (2H diagonal carry). 2H carry
          // is allowed a higher delta (raw seat tip-up trail) than ready.
          if (item === ofArch[0]) {
            const effective = baseHold ? mergeHold(baseHold, wa.hold ?? null) : null;
            const offLen = effective ? holdOffsetLen(effective) : 0;
            const twoHand = ofArch.some((i) => i.doc.weapon?.grip === "two-hand");
            const checkStanceGrip = (pose: "carry" | "ready", grip: [number, number, number] | undefined) => {
              const mag = gripDeltaMagnitude(grip);
              const cap =
                pose === "carry" && twoHand ? GRIP_DELTA_WARN_2H_CARRY : GRIP_DELTA_WARN;
              if (mag > cap && offLen <= OFFSET_WARN) {
                warnings.push({
                  file: raw.anim.file,
                  path: `archetypes.${archId}.${pose}.grip`,
                  message:
                    `|grip|=${mag.toFixed(2)} is a large stance rotation delta (prefer ≤${cap}` +
                    (pose === "carry" && twoHand ? " for 2H carry" : "") +
                    `) — fix item hold.rot for base seating; stance grip is only a pose delta`,
                });
              }
            };
            checkStanceGrip("carry", wa.carry?.grip as [number, number, number] | undefined);
            checkStanceGrip("ready", wa.ready?.grip as [number, number, number] | undefined);
          }
        }
      }
    }
  }

  return { errors, warnings };
}

// ---------------------------------------------------------------------------

export function formatIssues(issues: ContentIssue[]): string {
  return issues.map((i) => `${i.file}: ${i.path ? i.path + " → " : ""}${i.message}`).join("\n");
}

/** Fail-fast helper for load.ts: throw with every error listed. */
export function assertValid(res: ValidationResult): void {
  if (res.errors.length) {
    throw new Error(`content validation failed (${res.errors.length} error${res.errors.length === 1 ? "" : "s"}):\n${formatIssues(res.errors)}`);
  }
}
